import React, { Component, Fragment } from "react";
import {
	Table,
	Row,
	Button,
	Icon,
	Form,
	Popconfirm,
	Spin,
	Divider,
	Tooltip
} from "antd";
import moment from "moment";
import { ModuleMap } from "common/modules";
import { toJS } from "mobx";
import { inject, observer } from "mobx-react";
import _ from "lodash";
import AddEditProjectDialog from "./AddEditProjectDialog";
import RankerXLink from "components/RankerXLink";

const FormItem = Form.Item;

@inject("rootStore")
@observer
class ProjectList extends Component {
	showAddProjectDialog = () => {
		const {
			rootStore: { campaignStore }
		} = this.props;

		campaignStore.showAddProjectDialog();
	};

	showRenameProjectDialog = e => {
		const {
			rootStore: { campaignStore }
		} = this.props;
		campaignStore.showRenameProjectDialog({
			id: e.currentTarget.dataset.id,
			name: e.currentTarget.dataset.name,
			module: e.currentTarget.dataset.module
		});
	};

	showDuplicateProjectDialog = e => {
		const {
			rootStore: { campaignStore }
		} = this.props;
		campaignStore.showDuplicateProjectDialog({
			id: e.currentTarget.dataset.id,
			name: "Copy of " + e.currentTarget.dataset.name,
			module: e.currentTarget.dataset.module
		});
	};

	scheduleStatusFormatter = (text, project) => {
		const schedule = project.schedule;
		if (schedule == null) {
			return null;
		}
		return schedule.status;
	};

	scheduleDescriptionRenderer = (text, project) => {
		if (project.schedule == null) {
			return null;
		}
		let description = "";
		const schedule = project.schedule;
		if (schedule.durationType == "Spread" && schedule.nDay > 1) {
			description = `For ${schedule.nDay} days, starting `;
		} else {
			description = "Starting ";
		}

		if (schedule.scheduleType == "FixedDate") {
			const startDateString = schedule.startDate;
			const startDate = moment.utc(startDateString, "DD/MM/YYYY HH:mm");
			startDate.local();
			description += `at ${startDate.format("DD/MM/YYYY HH:mm")}`;
		} else {
			const sourceProjectId = schedule.sourceProjectId;
			if (sourceProjectId != 0) {
				const foundIndex = this.props.projects.findIndex(
					project => project.id == sourceProjectId
				);
				if (foundIndex > -1) {
					const sourceProject = this.props.projects[foundIndex];
					description += `after project ${sourceProject.name}`;
				}	
			} else {
				const sourceProjectIds = schedule.sourceProjectIds;
				let sourceProjects = this.props.projects.filter(project => {
					const foundIndex = sourceProjectIds.findIndex(
						sourceProjectId => project.id == sourceProjectId
					);
					if (foundIndex > -1) {
						return true;
					} else {
						return false;
					}
				})
				let sourceProjectNames = sourceProjects.map(project => {
					return project.name;
				})

				description += `after projects: ${_.join(sourceProjectNames, ", ")}`;
				// console.log("Source projects are", sourceProjects);
			}
		}
		return description;
	};

	columns = [
		{
			title: "ID",
			dataIndex: "id",
			width: 120,
			defaultSortOrder: "descend",
			sorter: (a, b) => a.id - b.id
		},
		{
			title: "Name",
			dataIndex: "name",
			sorter: (a, b) => (a.name > b.name ? 1 : -1),
			render: (text, record) => {
				if (record.module == "AccountCreation") {
					return (
						<RankerXLink
							routeName="acProject"
							params={{ id: record.id.toString() }}
						>
							{text}
						</RankerXLink>
					);
				} else if (record.module == "Indexer") {
					return (
						<RankerXLink
							routeName="indexerProject"
							params={{ id: record.id.toString() }}
						>
							{text}
						</RankerXLink>
					);
				} else if (record.module == "Private" || record.module == "PrivateBookmark") {
					return (
						<RankerXLink
							routeName="privateProject"
							params={{ id: record.id.toString() }}
						>
							{text}
						</RankerXLink>
					);
				} else {
					return (
						<RankerXLink
							routeName="postingProject"
							params={{ id: record.id.toString() }}
						>
							{text}
						</RankerXLink>
					);
				}
			}
		},
		{
			title: "Action",
			width: 100,
			render: (text, record) => {
				let project = record;
				return (
					<span>
						<Tooltip title="Rename">
							<a
								type="primary"
								data-name={project.name}
								data-id={project.id}
								data-module={project.module}
								onClick={this.showRenameProjectDialog}
							>
								<Icon type="edit" />
							</a>
						</Tooltip>
						<Divider type="vertical" />
						<Tooltip title="Duplicate">
							<a
								type="primary"
								data-name={project.name}
								data-id={project.id}
								data-module={project.module}
								onClick={this.showDuplicateProjectDialog}
							>
								<Icon type="copy" theme="outlined" />
							</a>
						</Tooltip>
					</span>
				);
			}
		},
		{
			title: "Module",
			dataIndex: "module",
			width: 200,
			sorter: (a, b) => (a.module > b.module ? 1 : -1),
			render: text => ModuleMap[text]
		},
		{
			title: "Status",
			dataIndex: "status",
			width: 130,
			render: text => {
				let backgroundColor = 'rgb(17, 119, 51)';
				let additionalClass = "ant-tag-has-color";
				switch (text) {
					case "COMPLETED": 
						backgroundColor = 'rgb(120, 28, 129)';
						break;
					case "FAILED":
						backgroundColor = 'rgb(255, 85, 0)';
						break;
					case "STOPPED": 
					case "NEW":
						backgroundColor = '#fafafa';
						additionalClass = "";
						break;						
					case "RUNNING":
					case "STARTING":
					case "STOPPING":
					case "STARTING_DOWNLOADING":
					case "DOWNLOADING":
					case "STOPPING_DOWNLOADING":
					case "STARTING_VERIFYING":
					case "VERIFYING":
					case "STOPPING_VERIFYING":
						backgroundColor = 'rgb(16, 142, 233)';
						break;
				}
				return <div className={"ant-tag " + additionalClass} style={{backgroundColor: backgroundColor}}>{text}</div>;
			}
		},
		{
			title: "Schedule",
			width: 150,
			render: this.scheduleStatusFormatter
		},
		{
			title: "Schedule Description",
			render: this.scheduleDescriptionRenderer
		}
	];

	deleteProjects = () => {
		const {
			rootStore: { campaignStore }
		} = this.props;

		campaignStore.remoteDeleteProjects();
	};

	render() {
		const {
			rootStore: { campaignStore }
		} = this.props;
		const selectedRowKeys = campaignStore.ui.selectedProjectKeys;

		const rowSelection = {
			selectedRowKeys: selectedRowKeys,
			onChange: (selectedRowKeys, selectedRows) => {
				campaignStore.selectProjects(selectedRowKeys);
			}
		};

		const spinning = campaignStore.ui.deletingProjects == true;

		return (
			<Spin spinning={spinning}>
				<Row>
					<Form layout="inline" style={{ marginBottom: 8 }}>
						<FormItem>
							<Button type="primary" onClick={this.showAddProjectDialog}>
								<Icon type="plus-square-o" />
							</Button>
						</FormItem>
						<FormItem>
							<Popconfirm
								title="Do you want to delete the selected projects?"
								placement="bottom"
								onConfirm={this.deleteProjects}
							>
								<Button type="primary" disabled={selectedRowKeys.length == 0}>
									<Icon type="delete" />
								</Button>
							</Popconfirm>
						</FormItem>
					</Form>
				</Row>
				<Row>
					<Table
						rowKey="id"
						columns={this.columns}
						rowSelection={rowSelection}
						dataSource={this.props.projects}
						pagination={
							{
								pageSize: 100
							}
						}
					/>
				</Row>
			</Spin>
		);
	}
}

@inject("rootStore")
@observer
export default class ProjectsTab extends Component {
	showRenameProjectDialog = () => {
		// console.log("Show rename project dialog");
	};

	createProject = payload => {
		// console.log("Creating project");
	};

	render() {
		const {
			rootStore: { campaignStore }
		} = this.props;

		const projects = toJS(campaignStore.projects);

		return (
			<Fragment>
				<AddEditProjectDialog />
				<ProjectList projects={projects} />
			</Fragment>
		);
	}
}
